import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Crest } from '@/components/Brand';
import { CheckCircle2, AlertTriangle, Clock } from 'lucide-react';

function formatRemaining(secs) {
  if (secs <= 0) return '0s';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  // cooldown is a number of seconds remaining, or null
  const [cooldown, setCooldown] = useState(null);

  // Tick down the cooldown banner once per second while it's active.
  useEffect(() => {
    if (cooldown === null) return undefined;
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => (c === null ? null : Math.max(0, c - 1))), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      if (typeof data?.cooldown_seconds === 'number') {
        setCooldown(data.cooldown_seconds);
        setSent(false);
      } else {
        setCooldown(null);
        setSent(true);
      }
    } catch {
      // Even on network errors keep messaging generic.
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  const cooldownActive = cooldown !== null && cooldown > 0;
  const disableSubmit = busy || cooldownActive;

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-trjlight flex items-center justify-center p-4 sm:p-6" data-testid="forgot-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 max-w-md w-full">
        <Crest size="lg" className="mx-auto" />
        <h1 className="font-display font-semibold text-2xl text-navy mt-4 text-center">Reset your password</h1>

        {cooldownActive && (
          <div
            className="mt-5 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-4 flex gap-3"
            role="alert"
            data-testid="forgot-cooldown-banner"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed">
              <div className="font-semibold mb-1">Please wait before trying again</div>
              <p>
                Too many password reset requests have been submitted. Please wait approximately
                1 hour before trying again. If you continue experiencing issues, contact{' '}
                <a href="mailto:support@trjacademy.com" className="font-semibold underline">support@trjacademy.com</a>.
              </p>
              <div
                className="mt-3 inline-flex items-center gap-1.5 bg-amber-100 text-amber-900 rounded-full px-3 py-1 text-xs font-semibold"
                data-testid="forgot-cooldown-remaining"
              >
                <Clock className="h-3.5 w-3.5" />
                Try again in {formatRemaining(cooldown)}
              </div>
            </div>
          </div>
        )}

        {!sent ? (
          <>
            {!cooldownActive && (
              <p className="text-sm text-slate-600 mt-3 text-center">
                Enter the email associated with your account and we&apos;ll send a secure reset link.
              </p>
            )}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  data-testid="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={cooldownActive}
                />
              </div>
              <Button
                type="submit"
                className="btn-navy w-full h-12"
                disabled={disableSubmit}
                data-testid="forgot-submit"
              >
                {busy ? 'Sending…' : cooldownActive ? `Try again in ${formatRemaining(cooldown)}` : 'Send reset link'}
              </Button>
            </form>
            <p className="text-sm text-slate-500 text-center mt-6">
              Remembered it? <Link to="/login" className="text-navy font-semibold hover:text-gold">Log in</Link>
            </p>
          </>
        ) : (
          <div className="text-center mt-6" data-testid="forgot-sent">
            <CheckCircle2 className="h-12 w-12 text-trjgreen mx-auto" />
            <h2 className="font-display font-semibold text-navy text-lg mt-3">Check your inbox</h2>
            <p className="text-sm text-slate-600 mt-2">
              If an account exists for <span className="font-semibold text-navy">{email}</span>, a password reset link has been sent. It expires in 30 minutes.
            </p>
            <Link to="/login" className="inline-block mt-6 text-navy font-semibold hover:text-gold">Back to log in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
